import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ipDetailsList } from '../../actions/inventory';
import { useDispatch, useSelector } from 'react-redux';

const DataCard = ({ title, children }) => (
  <div className="card mb-4">
    <div className="header">
      <div className="flex items-center mb-2">
        <h2>{title}</h2>
      </div>
    </div>
    <div className="body">{children}</div>
  </div>
);

const DataTable = ({ data, title }) => {
  if (!data || !Array.isArray(data) || data.length === 0) return null;

  const columns = Object.keys(data[0]);

  return (
    <DataCard title={title}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse table-auto">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column} className="p-2 border text-left">
                  {column.replace(/_/g, ' ').toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column} className="p-2 border">
                    {typeof row[column] === 'object' ?
                      JSON.stringify(row[column]) :
                      String(row[column] ?? 'N/A')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DataCard>
  );
};

const HostDetails = () => {
  const { id, ip } = useParams();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const ipDetails = useSelector((state) => state?.inventory?.ipDetails?.data);
  const _id = useSelector(state => {
      return state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
  });
  useEffect(() => {
    const fetchData = async () => {
      if (ip && _id) {
        try {
          await ipDetailsList(_id, ip, dispatch);
        } catch (error) {
          // console.error removed
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [id, ip, dispatch]);

  if (loading) {
    return (
      <div className="container-fluid">
        <DataCard title="Loading">
          <p>Loading IP details...</p>
        </DataCard>
      </div>
    );
  }

  if (!ipDetails?.ipInfo) {
    return (
      <div className="container-fluid">
        <DataCard className="mt-5">
          <p className="mb-0 text-center">No IP information available.</p>
        </DataCard>
      </div>
    );
  }

  const { ipInfo } = ipDetails;

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (Array.isArray(value)) return value.join(', ') || 'N/A';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const renderKeyValueSection = (data, title) => {
    if (!data) return null;

    return (
      <DataCard title={title}>
        <div className="space-y-2">
          {Object.entries(data).map(([key, value]) => (
            value !== undefined && (
              <div key={key} className="d-flex justify-content-between border-bottom pb-2">
                <span className="text-muted text-capitalize font-medium">
                  {key.replace(/_/g, ' ')}
                </span>
                <span className="text-right">
                  {typeof value === 'object' && value !== null ? (
                    <pre className="p-2 rounded text-sm" style={{ color: "#000", fontWeight: "bold" }}>
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  ) : (
                    formatValue(value)
                  )}
                </span>
              </div>
            )
          ))}
        </div>
      </DataCard>
    );
  };

  const renderTags = () => {
    if (!Array.isArray(ipInfo.tags)) return null;

    return (
      <DataCard title="Tags">
        <div className="flex flex-wrap gap-2">
          {ipInfo.tags.map((tag) => (
            <span key={tag} className="px-3 py-1 rounded" style={{ backgroundColor: "yellow", color: "#000", fontWeight: "bold" }}>
              {tag}
            </span>
          ))}
        </div>
      </DataCard>
    );
  };

  const renderPorts = () => {
    if (!Array.isArray(ipInfo.ports)) return null;

    return (
      <DataCard title="Open Ports">
        <div className="flex flex-wrap gap-2">
          {ipInfo.ports.map((portData, index) => {
            const port = typeof portData === 'object' ? portData.port : portData;
            const service = typeof portData === 'object' ? portData.service : null;
            
            return (
              <span key={index} className="bg-blue-500  px-3 py-1 rounded">
                {port}{service ? ` (${service})` : ''}
              </span>
            );
          })}
        </div>
      </DataCard>
    );
  };

  // Prepare data for tables
  const prepareTableData = () => {
    const tableData = [];
    if (Array.isArray(ipInfo.data)) {
      ipInfo.data.forEach((item) => {
        const row = {
          ip: item.ip_str,
          port: item.port,
          transport: item.transport,
          timestamp: item.timestamp,
          status: item.http?.status,
          server: item.http?.server,
          location: `${item.location?.city || ''}, ${item.location?.country_name || ''}`,
          organization: item.org,
          cloud_provider: item.cloud?.provider,
          cloud_service: item.cloud?.service,
        };
        tableData.push(row);
      });
    }
    return tableData;
  };

  return (
    <div className="container-fluid">
      <div className="row clearfix mt-5">
        {/* Main Information Table */}
        {/* <div className="col-lg-12">
          <DataTable 
            data={prepareTableData()} 
            title="IP Information Overview"
          />
        </div> */}

        {/* Basic Information Cards */}
        
        <div className="col-lg-6 col-md-12">
          {renderKeyValueSection({
            IP: ipInfo.ip_str,
            Organization: ipInfo.org,
            ISP: ipInfo.isp,
            ASN: ipInfo.asn,
            'Country Name': ipInfo.country_name,
            'Country Code': ipInfo.country_code,
            City: ipInfo.city,
            'Region Code': ipInfo.region_code,
            'Last Update': ipInfo.last_update
          }, 'Basic Information')}
          {renderPorts()}
          {renderTags()}
        </div>

        {/* Additional Information Cards */}
        <div className="col-lg-6 col-md-12">
          {renderKeyValueSection(ipInfo.data?.[0]?.cloud, 'Cloud Information')}
          {renderKeyValueSection(ipInfo.data?.[0]?.location, 'Location Information')}
          {renderKeyValueSection({
            domains: ipInfo.domains,
            hostnames: ipInfo.hostnames
          }, 'Network Information')}
        </div>

        {/* HTTP Information */}
        <div className="col-lg-12">
          {ipInfo.data?.map((item, index) => (
            item.http && (
              <DataCard key={index} title={`HTTP Information (Port ${item.port})`}>
                <div className="space-y-2">
                  {Object.entries(item.http).map(([key, value]) => (
                    <div key={key} className="d-flex justify-content-between border-bottom pb-2">
                      <span className="text-muted text-capitalize font-medium">
                        {key.replace(/_/g, ' ')}
                      </span>
                      <span className="text-right">
                        {formatValue(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </DataCard>
            )
          ))}
        </div>

        {/* Raw Data */}
        {/* <div className="col-lg-12">
          <DataCard title="Raw Data">
            <pre className="bg-gray-100 p-4 rounded overflow-auto text-sm">
              {JSON.stringify(ipInfo, null, 2)}
            </pre>
          </DataCard>
        </div> */}
      </div>
    </div>
  );
};

export default HostDetails;